import { isEmpty, map, size } from 'lodash'
import React from 'react'
import { RouteComponentProps } from 'react-router'
import { useEffectOnce } from 'react-use'
import {
  TableInstance,
  useColumnOrder,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import { UseRowSelectInstanceProps } from 'react-table'
import { makeStyles } from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import RemoveCircleOutlineOutlinedIcon from '@material-ui/icons/RemoveCircleOutlineOutlined'

import { TableMenuButton, TablePopoverButton, Loading, ConfirmDialog } from 'civic-champs-shared'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { useConditionalSelectColumn, useGetColumnState } from 'core/table/table-hooks'
import DEFAULT_FILTERS, { useGetGlobalFilter } from 'core/table/filters'
import { ExtendedPagedTable, useFiltersFromQuery } from 'core/table/components'
import PageHeader from 'App/components/PageHeader'
import CUSTOM_FILTERS from 'champion/utils/filters'
import { ExportCheckInsButton } from './components/ExportCheckInsButton'
import { useCheckInsColumns, getOperatorOptions, useStyles as useColumnStyles } from './hooks/useCheckInsColumns'
import useCheckInsCollection, { BulkCheckOutResult } from './hooks/useCheckInsCollection'
import { CheckIn } from './types'

const tableName = 'checkIns'
const filterTypes = { ...DEFAULT_FILTERS, ...CUSTOM_FILTERS }

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    height: 'fit-content',
  },
}))

const useHandleBulkCheckOut = (
  table: TableInstance<CheckIn>,
  bulkCheckOut: (checkIns: CheckIn[]) => Promise<BulkCheckOutResult>,
) => {
  return async () => {
    const rowSelector = table as unknown as UseRowSelectInstanceProps<CheckIn>
    const checkIns = map(rowSelector.selectedFlatRows, row => row.original)

    if (isEmpty(checkIns)) return

    const { success } = await bulkCheckOut(checkIns)
    if (success) rowSelector.toggleAllRowsSelected(false)
  }
}

const useHandleBulkDelete = (
  table: TableInstance<CheckIn>,
  bulkDelete: (checkIns: CheckIn[]) => Promise<BulkCheckOutResult>,
) => {
  const showPrompt = useShowPrompt(ConfirmDialog)

  return async () => {
    const confirmed = await showPrompt({
      text: 'Deleted check-ins are lost and will not create an activity. This action is rarely necessary.',
      title: 'Are you sure you want to delete selected check-ins?',
      confirmText: 'Delete Selected Check-Ins',
      cancelText: 'Do Not Delete',
    })

    if (!confirmed) return

    const rowSelector = table as unknown as UseRowSelectInstanceProps<CheckIn>
    const checkIns = map(rowSelector.selectedFlatRows, row => row.original)

    if (isEmpty(checkIns)) return

    const { success } = await bulkDelete(checkIns)
    if (success) rowSelector.toggleAllRowsSelected(false)
  }
}

const CheckInsComponent = (props: RouteComponentProps) => {
  const { location, history } = props

  const { filters } = useFiltersFromQuery(location.search, getOperatorOptions)

  const [{ checkIns, loading, busy }, { refresh, bulkCheckOut, bulkDelete }] = useCheckInsCollection()

  const [fetchColumnState, { loading: columnStateLoading, result: columnState }] = useGetColumnState(tableName)
  useEffectOnce(() => {
    fetchColumnState(tableName)
  })

  const columnStyles = useColumnStyles()
  const columns = useCheckInsColumns()
  const globalFilter = useGetGlobalFilter({
    columns,
    filterTypes,
    getOperatorOptions,
  })

  const table = useTable<CheckIn>(
    {
      initialState: {
        hiddenColumns: ['firstName', 'lastName', 'email', 'mobile', 'homePhoneNumber', 'groups', 'tags'],
        // @ts-ignore
        globalFilter: '',
        filters,
      },
      globalFilter,
      filterTypes,
      data: checkIns,
      columns,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useConditionalSelectColumn(() => true, columnStyles.selectColumn),
  )

  const selectedRows = (table as unknown as UseRowSelectInstanceProps<CheckIn>).selectedFlatRows
  const selectedRowCount = size(selectedRows)

  const handleBulkDelete = useHandleBulkDelete(table, bulkDelete)

  const classes = useStyles()

  const handleCheckOut = useHandleBulkCheckOut(table, bulkCheckOut)

  if (loading || columnStateLoading) return <Loading />

  return (
    <div className={classes.container}>
      <ExtendedPagedTable
        history={history}
        location={location}
        table={table}
        columns={columns}
        getOperatorOptions={getOperatorOptions}
        columnState={columnState}
        tableName={tableName}
        compactView
        expandable={false}
        useGlobalSearch
        useFilters
        buttons={
          <>
            <TableMenuButton
              startIcon={<CheckIcon />}
              onClick={handleCheckOut}
              disabled={!selectedRowCount || busy}
              rightMargin
            >
              Check-Out
            </TableMenuButton>
            <ExportCheckInsButton data={selectedRows} disabled={!selectedRowCount || busy} />
          </>
        }
        popover={
          <>
            <TablePopoverButton
              startIcon={<RemoveCircleOutlineOutlinedIcon />}
              disabled={!selectedRowCount || busy}
              onClick={handleBulkDelete}
            >
              Cancel Check-Ins
            </TablePopoverButton>
          </>
        }
      />
    </div>
  )
}

const CheckIns = (props: RouteComponentProps) => (
  <PageHeader title="Check-Ins" subTitle="View and manage volunteers currently checked-in">
    <CheckInsComponent {...props} />
  </PageHeader>
)

export default CheckIns
